import { ErrorResponse } from "../models/apiResponse";

export type HttpErrorKind = "http" | "network" | "parse" | "config";

const MAX_RAW_LENGTH = 2048;

type HttpErrorInit = {
  kind: HttpErrorKind;
  message: string;
  url: string;
  status?: number;
  statusText?: string;
  code?: string;
  body?: unknown;
  raw?: string;
  cause?: unknown;
};

// A failed request. `status` is 0 for anything that never got an HTTP response.
export class HttpError extends Error {
  readonly isHttpError = true as const;
  readonly kind: HttpErrorKind;
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly code?: string;
  readonly body?: unknown;
  readonly raw?: string;
  readonly cause?: unknown;

  constructor(init: HttpErrorInit) {
    super(init.message);
    this.name = "HttpError";
    this.kind = init.kind;
    this.status = init.status ?? 0;
    this.statusText = init.statusText ?? "";
    this.url = init.url;
    this.code = init.code;
    this.body = init.body;
    this.raw = init.raw;
    this.cause = init.cause;

    // Jest/babel may transpile below the tsconfig target, which breaks the
    // prototype chain for subclassed builtins.
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  static network(url: string, cause: unknown): HttpError {
    return new HttpError({
      kind: "network",
      message:
        cause instanceof Error && cause.message
          ? cause.message
          : "Network request failed",
      url,
      cause,
    });
  }

  static parse(url: string, raw: string): HttpError {
    return new HttpError({
      kind: "parse",
      message: "Invalid JSON in response body",
      url,
      raw: truncate(raw),
    });
  }

  static config(url: string, message: string): HttpError {
    return new HttpError({ kind: "config", message, url });
  }
}

export const isHttpError = (error: unknown): error is HttpError =>
  typeof error === "object" &&
  error !== null &&
  (error as HttpError).isHttpError === true;

const truncate = (raw: string): string =>
  raw.length > MAX_RAW_LENGTH ? `${raw.slice(0, MAX_RAW_LENGTH)}…` : raw;

const isErrorEnvelope = (
  data: any
): data is Partial<ErrorResponse> & { raw: string } =>
  typeof data === "object" && data !== null && typeof data.raw === "string";

// Reads the body once as text so a non-JSON error page (nginx 502, HTML
// redirect, empty body) cannot throw a SyntaxError out of the error path.
export async function toHttpError(
  response: Response,
  url: string
): Promise<HttpError> {
  const raw = await response.text().catch(() => "");

  let body: unknown;
  try {
    body = raw ? JSON.parse(raw) : undefined;
  } catch {
    body = undefined;
  }

  const base = {
    kind: "http" as const,
    url,
    status: response.status,
    statusText: response.statusText,
    body,
    raw: truncate(raw),
  };

  if (isErrorEnvelope(body)) {
    return new HttpError({
      ...base,
      message: body.devMsg || body.raw,
      code: body.mappedErrorCode,
    });
  }

  return new HttpError({
    ...base,
    message: `HTTP ${response.status} ${response.statusText || ""}`.trim(),
  });
}
